/**
 * Hook for detecting and handling workflow events from messages
 * Automatically syncs message metadata with workflow store
 */

import { useEffect } from 'react';
import { useMessageStore } from '@/stores/messageStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { WorkflowEventType } from '@/types/workflow';

interface MessageWithWorkflowData {
  id: string;
  role: string;
  metadata?: {
    workflowEvent?: WorkflowEventType;
    workflowData?: any;
  };
}

export function useWorkflowEvents(conversationId: string) {
  const { getMessagesForConversation } = useMessageStore();
  const { handleWorkflowEvent, setWorkflowEnabled, initWorkflow } = useWorkflowStore();

  useEffect(() => {
    if (!conversationId) return;

    const messages = getMessagesForConversation(conversationId) as MessageWithWorkflowData[];

    // Check for workflow events in messages
    messages.forEach((message) => {
      if (message.metadata?.workflowEvent && message.metadata?.workflowData) {
        const event = {
          type: message.metadata.workflowEvent,
          step: message.metadata.workflowData.step,
          data: message.metadata.workflowData,
          timestamp: new Date(),
        };

        console.log('🎬 Detected workflow event in message:', message.id, event);
        handleWorkflowEvent(conversationId, event);
      }
    });

    // Auto-enable workflow if we detect any video-related activity
    const hasVideoActivity =
      messages.some(
        (msg) =>
          msg.metadata?.workflowEvent ||
          (msg.role === 'user' &&
            (msg as any).content?.toLowerCase().includes('video')) ||
          (msg as any).attachments?.some((att: any) => att.type === 'video')
      );

    if (hasVideoActivity) {
      initWorkflow(conversationId);
      setWorkflowEnabled(conversationId, true);
    }
  }, [conversationId, getMessagesForConversation, handleWorkflowEvent, setWorkflowEnabled, initWorkflow]);
}

/**
 * Detect workflow triggers from user messages
 * Returns true if message should trigger workflow initialization
 */
export function detectWorkflowTrigger(message: any): boolean {
  // Check for file uploads
  if (message.attachments && message.attachments.length > 0) {
    const hasRelevantFiles = message.attachments.some(
      (att: any) =>
        att.type === 'pdf' ||
        att.type === 'document' ||
        att.type === 'transcript' ||
        att.name?.toLowerCase().includes('transcript') ||
        att.name?.toLowerCase().includes('document')
    );
    if (hasRelevantFiles) return true;
  }

  // Check for video-related keywords
  const videoKeywords = [
    'create video',
    'make video',
    'generate video',
    'video project',
    'create slides',
    'generate slides',
    'plan video',
    'storyboard',
  ];

  const content = message.content?.toLowerCase() || '';
  return videoKeywords.some((keyword) => content.includes(keyword));
}

/**
 * Parse workflow data from AI message content
 * Extracts structured workflow information from AI responses
 */
export function parseWorkflowDataFromMessage(message: any): {
  event?: WorkflowEventType;
  data?: any;
} | null {
  const content = message.content || '';

  // Pattern matching for workflow events
  const patterns = [
    {
      regex: /analyzing\s+(\d+)\s+resources?/i,
      event: 'resource_analysis_started' as WorkflowEventType,
      extract: (match: RegExpMatchArray) => ({
        totalResources: parseInt(match[1]),
      }),
    },
    {
      regex: /analysis\s+complete|analyzed\s+all\s+resources/i,
      event: 'resource_analysis_completed' as WorkflowEventType,
    },
    {
      regex: /generating\s+plan|creating\s+video\s+plan/i,
      event: 'planning_started' as WorkflowEventType,
    },
    {
      regex: /plan\s+generated|plan\s+created|here'?s\s+the\s+plan/i,
      event: 'plan_generated' as WorkflowEventType,
    },
    {
      regex: /generating\s+slide\s+(\d+)/i,
      event: 'slide_progress' as WorkflowEventType,
      extract: (match: RegExpMatchArray) => ({
        currentSlide: parseInt(match[1]),
      }),
    },
    {
      regex: /slide\s+(\d+)\s+complete|completed\s+slide\s+(\d+)/i,
      event: 'slide_completed' as WorkflowEventType,
      extract: (match: RegExpMatchArray) => ({
        slideNumber: parseInt(match[1] || match[2]),
      }),
    },
    {
      regex: /all\s+slides\s+generated|slides\s+complete/i,
      event: 'slide_generation_completed' as WorkflowEventType,
    },
  ];

  for (const pattern of patterns) {
    const match = content.match(pattern.regex);
    if (match) {
      return {
        event: pattern.event,
        data: pattern.extract ? pattern.extract(match) : {},
      };
    }
  }

  return null;
}
